import { useAuth0 } from '@auth0/auth0-react';
import { request } from '@core/api';
import { errorBox, successBox } from '@core/components/AlertBox';
import { SelectButton } from '@core/components/SelectButton';
import { getIsFeatureEnabled } from '@core/flagsmith';
import { useLocalStorage } from '@core/logic/hooks/useLocalStorage';
import { useAppDispatch } from '@core/redux/hooks';
import { RpcRestError } from '@deepup/api-utils';
import { ArrowUpLine } from '@deepup/icons';
import { useFlags } from '@flagsmith/flagsmith/react';
import {
  Button,
  CircularProgress,
  FormLabel,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { trenchThunks } from '@trenches/redux';
import { viewer3dSelectors } from '@viewer3D/redux';
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  cleanExternalDataCache,
  GeoJsonFeatureCollection,
  useCreateTrenchesFromGeoJson,
} from './hooks';

type UploadType = 'external' | 'geojson';

export const getErrorMessage = (
  error: unknown,
  fallbackMessage = 'Error uploading data',
): string => {
  if (error instanceof RpcRestError && error.body?.message) {
    return error.body.message;
  }
  if (typeof error === 'object' && error !== null) {
    const errorObj = error as {
      body?: { message?: string };
      response?: { data?: { message?: string; detail?: string } };
      message?: string;
    };
    if (errorObj.body?.message) {
      return errorObj.body.message;
    }
    if (errorObj.response?.data?.message) {
      return errorObj.response.data.message;
    }
    if (errorObj.response?.data?.detail) {
      return errorObj.response.data.detail;
    }
    if (typeof errorObj.message === 'string' && errorObj.message.trim().length > 0) {
      return errorObj.message;
    }
  }
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return fallbackMessage;
};

export const UploadButton = () => {
  const [inProgress, setInProgress] = useState(false);
  const projectId = useSelector(viewer3dSelectors.selectedProjectId)!;
  const dispatch = useAppDispatch();
  const { createTrenchesFromGeoJson } = useCreateTrenchesFromGeoJson();
  const { user } = useAuth0();
  const { geojson_upload_enabled } = useFlags(['geojson_upload_enabled']);
  const isGeoJsonUploadEnabled = getIsFeatureEnabled(geojson_upload_enabled, user);

  const [uploadType, setUploadType] = useLocalStorage<UploadType>('UploadButton-value', 'external');
  const inputRef = useRef<HTMLInputElement>(null);
  const uploadTypeRef = useRef(uploadType);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('xl'));

  const uploadExternalData = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await request('POST', {
      path: ['projects', projectId, 'externalData'],
      body: formData,
    });
    if (response instanceof Error) {
      throw response;
    }
    cleanExternalDataCache();
  };

  const uploadGeoJsonTrenches = async (file: File) => {
    const fileContents = await file.text();
    const geoJson = JSON.parse(fileContents) as GeoJsonFeatureCollection;
    await createTrenchesFromGeoJson(projectId, geoJson);
    dispatch(
      trenchThunks.getMany({
        path: ['projects', projectId, 'trenches'],
      }),
    );
  };

  const handleUploadSelect = (value: UploadType) => {
    uploadTypeRef.current = value;
    setUploadType(value);
    inputRef.current?.click();
  };

  const handleUploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || inProgress) {
      return;
    }

    setInProgress(true);
    try {
      if (isGeoJsonUploadEnabled && uploadTypeRef.current === 'geojson') {
        await uploadGeoJsonTrenches(file);
        successBox('Success creating trenches from GeoJSON!');
      } else {
        await uploadExternalData(file);
        successBox('Success uploading file!');
      }
    } catch (error) {
      errorBox(getErrorMessage(error));
    } finally {
      e.target.value = '';
      setInProgress(false);
    }
  };

  if (!isGeoJsonUploadEnabled) {
    return (
      <FormLabel htmlFor="upload-external-data">
        {!isSmallScreen && (
          <Button
            variant="outlined"
            color="secondary"
            component="span"
            startIcon={
              inProgress ? (
                <CircularProgress color="inherit" size="1rem" />
              ) : (
                <ArrowUpLine fill="white" />
              )
            }
          >
            Upload
          </Button>
        )}
        {isSmallScreen && (
          <IconButton component="span">
            {inProgress ? (
              <CircularProgress color="inherit" size="1rem" />
            ) : (
              <ArrowUpLine fill="white" />
            )}
          </IconButton>
        )}
        <input
          id="upload-external-data"
          hidden
          accept=".geojson,application/geo+json,application/json"
          type="file"
          onChange={handleUploadFile}
        />
      </FormLabel>
    );
  }

  return (
    <>
      <SelectButton<UploadType>
        title="Upload"
        label="Upload"
        options={[
          { value: 'external', label: 'External data' },
          { value: 'geojson', label: 'GeoJSON' },
        ]}
        value={uploadType}
        onClick={handleUploadSelect}
        onChange={handleUploadSelect}
        isLoading={inProgress}
        labelIcon={
          inProgress ? (
            <CircularProgress color="inherit" size="1rem" />
          ) : (
            <ArrowUpLine fill="white" />
          )
        }
      />
      <input
        ref={inputRef}
        hidden
        accept=".geojson,application/geo+json,application/json"
        type="file"
        onChange={handleUploadFile}
      />
    </>
  );
};
